// @flow

import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled, { type StyledComponent } from 'styled-components';

import { __ } from '../../../i18n/l10n';
import type { RootState, ContentState, Content } from '../../../store/types';
import { sizes, colorPalette, media } from '../../../styles/GlobalStyle';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import { HtmlContentContainer } from './HtmlContentContainer';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import { Select } from './Select';

type PromotionProps = {
  className?: string,
  subject?: string,
};

const RawPromotion = ({ className, subject }: PromotionProps) => {
  const contentObj: ContentState = useSelector((state: RootState) => state.content);
  // @eslint-disable-next-line
  const [subjects, setSubjects] = useState<Content[]>([]);
  const [currentSubject, setCurrentSubject] = useState<string | null>(null);
  const tag = 'promotion';

  const onSelectChange = (event) => {
    const selectedSubject: Content | void = subjects?.find(item => item.key === event);
    if (selectedSubject) {
      setCurrentSubject(selectedSubject.key);
    }
  };

  useEffect(() => {
    const newSubjects = contentObj.contents.filter(item => item.tag === tag) || [];
    setSubjects(newSubjects);
    if (!currentSubject && newSubjects.length > 0) {
      setCurrentSubject(newSubjects[0].key);
    }
  }, [contentObj, currentSubject]);

  return (
    <div className={className}>
      <Select
        className={'promotion-select'}
        options={subjects.map((item) => {
          return { value: item.key, label: __(item.name) };
        })}
        value={subject}
        onChange={onSelectChange}
      />
      <HtmlContentContainer
        className={'promotion-content'}
        contentKey={currentSubject}
      />
    </div>
  );
};

export const Promotion: StyledComponent<PromotionProps, {}, typeof RawPromotion> = styled(RawPromotion)`
  position: relative;

  .promotion-select {
    position: fixed;
    top: 62px;
    left: ${sizes.padding};
    padding-top: 18px;
    width: calc(100% - ${sizes.padding} * 2);
    background-color: ${colorPalette.dark.active};

    &:before {
      content: '';
      position: absolute;
      bottom: -20px;
      display: block;
      width: 100%;
      height: 20px;
      background: #000000;
      background: linear-gradient(to top, rgba(0, 0, 0, 0) 0%, #000000 100%);
      user-select: none;
    }
  }

  .promotion-content {
    margin-top: 112px;

    br {
      content: '';
      margin: 2em;
      display: block;
      font-size: 24%;
    }

    ul {
      list-style: inherit;
      padding-left: 1.2em;
    }

    h1 {
      counter-reset: h2;
    }

    h2,
    h3,
    h4 {
      margin-top: 1.2em;
      margin-bottom: 0.8em;
    }

    h2 {
      counter-reset: h3;
      font-size: 1.3em;
      &:before {
        content: counter(h2) '. ';
        counter-increment: h2;
        color: inherit;
      }
    }

    h3 {
      counter-reset: h4;
      font-size: 1.2em;
      &:before {
        content: counter(h2) '. ' counter(h3) '. ';
        counter-increment: h3;
      }
    }

    h4 {
      counter-reset: h5;
      font-size: 1.1em;
      &:before {
        content: counter(h2) '. ' counter(h3) '. ' counter(h4) '. ';
        counter-increment: h4;
      }
    }
  }

  ${media.greaterThan('desktop')`
    .promotion-select {
      position: fixed;
      top: 138px;
      left: calc(${sizes.paddingBig} - 1px);
      padding-top: 12px;
      width: calc(100% - ${sizes.paddingBig} * 2 + 1px);
      background-color: ${colorPalette.dark.active};
    }

    .promotion-content {
      margin-top: 100px;
    }
  `}
`;
